import { css } from 'styled-components';

/**
 * DWEB-173 (v6.8.0) — prefers-reduced-transparency.
 *
 * The site handled prefers-reduced-motion everywhere and read this preference
 * nowhere, so a visitor who had asked their OS to reduce transparency still
 * got blur(35px) saturate(240%) on the header and the mobile nav, blur(40px)
 * saturate(300%) on the hero and blur(24px) on the DynaMite panel — 56
 * backdrop-filter elements on the homepage.
 *
 * WHY IT IS CHEAP NOW. DWEB-170 made the ground a flat #000000 painted by one
 * element with one child, so "what is behind the glass" has a known answer and
 * the opaque equivalent of each tint is arithmetic rather than guesswork: the
 * tint composited over black. Over the ground the fallback is therefore the
 * SAME luminance the glass already paints; it differs only where page content
 * scrolls behind a surface, which is exactly the case the preference is about.
 *
 * THE WASH STAYS, and that is a decision rather than an oversight. The ticket
 * offered "drop the wash layer (or leave it — it is a static gradient, not
 * transparency)". It is left: it is an opaque radial gradient painted ON the
 * ground, not a see-through effect, so it is not what the preference asks
 * about; and v6.7.0 measured that on black it is the only luminance the glass
 * rims have left to catch (panel edge vs ground, ΔL 18.5). Removing it would
 * degrade the very surfaces this fallback is meant to make readable, for a
 * visitor who never asked for that.
 *
 * THE HOVER CLAUSE IS LOAD-BEARING. HeroContainer re-declares
 * `backdrop-filter: blur(45px)` inside `&:hover, &:focus-within`, which is
 * (0,2,0) and would outrank a bare `backdrop-filter: none` at (0,1,0). The
 * nested `&:hover` here matches that specificity and, being emitted later,
 * wins. Without it the blur returns the moment the pointer lands.
 *
 * SCOPE, STATED PLAINLY: this covers the four surfaces DWEB-173 names — the
 * header, the mobile nav, the hero and the DynaMite panel. It does NOT cover
 * LiquidGlassInput (44 `!important`) or LiquidGlassSelect (23), whose
 * backdrop-filters are forced and which a media query at equal specificity
 * cannot outrank. Removing those was outside v6.8.0's fence; the residual
 * count under `reduce` is measured and recorded rather than left to be
 * discovered. e2e/glass-tint.spec.js asserts the four are actually `none`.
 *
 * @param {string} solid an OPAQUE colour: the surface's own tint composited
 *   over the ground. Never a translucent value — the point is to remove alpha.
 */
export const solidWhenReducedTransparency = (solid) => css`
  @media (prefers-reduced-transparency: reduce) {
    background-color: ${solid};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;

    &:hover,
    &:focus,
    &:focus-within {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
  }
`;

export default solidWhenReducedTransparency;
